import { cn } from "@/lib/utils";
import { useState } from "react";

/**
 * Shared newsletter signup section — "The Corgi Brief".
 *
 * Email-only sub: posts to `/api/public/etf-website/subscribe` with empty
 * names and `investor_type: "unknown"`. The Django `etf_website` public
 * router writes the row to the Neon `subscribers` table with
 * ON CONFLICT (email) updating updated_at.
 */
export function EtfNewsletter({ className }: { className?: string }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");
    setErrorMsg(null);
    try {
      const res = await fetch("/api/public/etf-website/subscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          first_name: "",
          last_name: "",
          email: email.trim(),
          investor_type: "unknown",
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        success?: true;
        error?: string;
      };
      if (!res.ok || !data.success) {
        setStatus("error");
        setErrorMsg(data.error ?? "Subscription failed. Please try again.");
        return;
      }
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again.");
    }
  };

  return (
    <section
      className={cn(
        "flex items-center justify-center px-10 py-14 border-b border-[var(--etf-border)] bg-[var(--etf-black2)] gap-12",
        // Canonical .newsletter responsive ladder (fund-page.css:1801/2182-2186/1994-1997):
        //   ≤1024: px reduces to 16px; gap stays 48px; row layout stays.
        //   ≤768:  flex-direction column; padding 40px 24px; gap stays 48px; ALIGN stays center.
        //   ≤480:  padding 32px 12px; gap 24px.
        // Live was forcing items-start + gap 28px on mobile (both wrong) and used px-5 (20px) instead of 24px.
        "max-[1024px]:px-4",
        "max-md:flex-col max-md:px-6 max-md:py-10",
        "max-[480px]:px-3 max-[480px]:py-8 max-[480px]:gap-6",
        className,
      )}
    >
      <div className="flex-1 max-w-[400px]">
        <div className="font-[family-name:var(--font-f37bolton)] text-4xl tracking-[-0.02em] leading-none text-[var(--etf-white)] mb-1.5 max-[480px]:text-[28px]">
          The <em className="not-italic text-[var(--etf-orange)]">Corgi</em> Brief
        </div>
        {/* Canonical .nl-desc: 14px lh inherits body 1.5. text-sm baked-in lh
            20px is the Tailwind shorthand bleed → explicit text-[14px] leading-[1.5].
            Drop redundant max-w (parent .nl-left already constrains to 400px). */}
        <div className="font-[family-name:var(--font-instrument)] text-[14px] leading-[1.5] text-[var(--etf-white2)] tracking-[0.03em]">
          Newsletter from the Corgi team delivering fund updates, data points, and top reads.
        </div>
      </div>
      <div className="flex flex-col flex-1 max-w-[440px] max-md:max-w-full max-md:w-full">
        <form className="flex" onSubmit={handleSubmit}>
          <input
            className="flex-1 px-[18px] py-3.5 font-[family-name:var(--font-instrument)] bg-[var(--etf-black3)] border border-[var(--etf-border)] border-r-0 text-[var(--etf-white)] text-[14px] leading-[1.5] outline-none transition-[border-color] duration-150 placeholder:text-[var(--etf-white3)] focus:border-[var(--etf-white2)] disabled:opacity-60"
            placeholder="your@email.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === "submitting" || status === "success"}
            required
            aria-label="Email address"
          />
          <button
            type="submit"
            disabled={status === "submitting" || status === "success"}
            className="px-6 py-3.5 font-[family-name:var(--font-instrument)] bg-[var(--etf-orange)] text-white border-none text-[14px] leading-[1.5] tracking-[0.1em] uppercase cursor-pointer transition-opacity duration-150 whitespace-nowrap hover:opacity-[0.88] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === "submitting"
              ? "Subscribing…"
              : status === "success"
                ? "Subscribed ✓"
                : "Subscribe →"}
          </button>
        </form>
        {status === "error" && errorMsg && (
          <div
            role="alert"
            className="mt-2 text-xs font-[family-name:var(--font-instrument)] text-[#ff6b6b] tracking-[0.02em]"
          >
            {errorMsg}
          </div>
        )}
        {status === "success" && (
          <output className="mt-2 block text-xs font-[family-name:var(--font-instrument)] text-[var(--etf-white2)] tracking-[0.02em]">
            Thanks — check your inbox for the next Corgi Brief.
          </output>
        )}
      </div>
    </section>
  );
}
